import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  FileText, Search, CheckCircle2, Clock, XCircle, Loader2,
  ChevronLeft, ChevronRight, Calendar, TrendingUp, Wallet,
  ArrowDownToLine, BarChart3, Filter, X,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/components/ui/select";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as settlementService from "@/shared/api/services/settlementService";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

.mst-root { font-family: 'Plus Jakarta Sans', sans-serif; color: #e2f4ff; padding-bottom: 60px; }
.mst-root * { box-sizing: border-box; }

/* ── Header ── */
.mst-h1 { font-size: clamp(1.5rem, 3vw, 1.8rem); font-weight: 900; color: #e2f4ff; letter-spacing: -0.035em; }
.mst-sub { color: #2a4a65; font-size: 13px; font-weight: 500; margin-top: 4px; }

/* ── Stat cards ── */
.mst-stat {
  background: linear-gradient(145deg, rgba(10,22,46,0.97), rgba(8,18,40,0.99));
  border: 1px solid rgba(34,211,238,0.08); border-radius: 22px; padding: 20px 22px;
  position: relative; overflow: hidden;
  transition: border-color 0.28s, box-shadow 0.28s, transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
  will-change: transform; cursor: default;
}
.mst-stat::before {
  content: ''; position: absolute; top: 0; left: 20%; right: 20%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent);
}
.mst-stat::after {
  content: ''; position: absolute; top: -16px; right: -16px;
  width: 100px; height: 100px; border-radius: 50%;
  background: radial-gradient(circle, var(--sg, rgba(34,211,238,0.08)), transparent 70%);
  pointer-events: none;
}
.mst-stat:hover {
  border-color: var(--sb, rgba(34,211,238,0.25));
  box-shadow: 0 10px 44px var(--ss, rgba(34,211,238,0.12));
  transform: translateY(-5px) scale(1.015);
}
.mst-stat:hover .mst-stat-icon { transform: scale(1.15) rotate(-8deg); }

.mst-stat-icon {
  width: 42px; height: 42px; border-radius: 13px; flex-shrink: 0; margin-bottom: 14px;
  display: flex; align-items: center; justify-content: center; position: relative; z-index: 1;
  transition: transform 0.32s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.28s;
}
.mst-stat-icon::after { content: ''; position: absolute; inset: 0; background: linear-gradient(145deg, rgba(255,255,255,0.12), transparent); border-radius: 13px; }
.mst-stat-lbl { font-size: 10px; font-weight: 800; color: #2a4a65; text-transform: uppercase; letter-spacing: 0.12em; margin-bottom: 6px; position: relative; z-index: 1; }
.mst-stat-val { font-family: 'JetBrains Mono', monospace; font-size: clamp(1.15rem, 2.5vw, 1.45rem); font-weight: 700; color: #e2f4ff; letter-spacing: -0.02em; position: relative; z-index: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ── Filter bar ── */
.mst-filter {
  background: linear-gradient(145deg, rgba(10,22,46,0.97), rgba(8,18,40,0.99));
  border: 1px solid rgba(34,211,238,0.08); border-radius: 18px; padding: 16px 20px;
  display: flex; flex-wrap: wrap; gap: 12px; align-items: center;
  position: relative; overflow: hidden;
}
.mst-filter::before {
  content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent);
}

.mst-search { position: relative; flex: 1; min-width: 200px; }
.mst-search input {
  width: 100%; background: rgba(0,0,0,0.3); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 12px; padding: 10px 14px 10px 40px;
  color: #e2f4ff; font-size: 13.5px; font-weight: 500;
  outline: none; font-family: 'Plus Jakarta Sans', sans-serif;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}
.mst-search input::placeholder { color: #1e3a55; }
.mst-search input:focus { border-color: rgba(34,211,238,0.38); background: rgba(0,0,0,0.4); box-shadow: 0 0 0 4px rgba(34,211,238,0.07); }
.mst-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #2a4a65; pointer-events: none; transition: color 0.2s; }
.mst-search:focus-within .mst-search-icon { color: #22d3ee; }

.mst-sel {
  background: rgba(0,0,0,0.3) !important; border: 1px solid rgba(34,211,238,0.1) !important;
  color: #e2f4ff !important; border-radius: 12px !important; min-width: 150px;
  font-family: 'Plus Jakarta Sans', sans-serif !important; font-weight: 500 !important;
  transition: border-color 0.22s !important;
}
.mst-sel:hover, .mst-sel[data-state=open] { border-color: rgba(34,211,238,0.35) !important; box-shadow: 0 0 16px rgba(34,211,238,0.08) !important; }

/* ── Table card ── */
.mst-table-card {
  background: linear-gradient(145deg, rgba(10,22,46,0.97), rgba(8,18,40,0.99));
  border: 1px solid rgba(34,211,238,0.08); border-radius: 22px; overflow: hidden;
  position: relative;
}
.mst-table-card::before {
  content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.2), transparent);
}
.mst-table { width: 100%; border-collapse: collapse; }
.mst-thead th {
  text-align: left; padding: 14px 16px;
  font-size: 10px; font-weight: 800; color: #2a4a65;
  text-transform: uppercase; letter-spacing: 0.1em;
  background: rgba(0,0,0,0.3); border-bottom: 1px solid rgba(34,211,238,0.06); white-space: nowrap;
}
.mst-row { border-bottom: 1px solid rgba(34,211,238,0.04); position: relative; cursor: default; }
.mst-row:last-child { border-bottom: none; }
.mst-row::after { content: ''; position: absolute; inset: 0; background: rgba(34,211,238,0.03); opacity: 0; transition: opacity 0.18s; pointer-events: none; }
.mst-row:hover::after { opacity: 1; }
.mst-td { padding: 15px 16px; vertical-align: middle; }

/* Settlement ID */
.mst-sid { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #c8dff0; font-weight: 600; }
.mst-sid-pill {
  display: inline-flex; align-items: center;
  max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  padding: 6px 10px; border-radius: 10px;
  background: rgba(34,211,238,0.06); border: 1px solid rgba(34,211,238,0.14);
}

/* Period */
.mst-period { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #5a8aaa; font-weight: 600; }
.mst-period-sub { margin-top: 4px; font-size: 11px; color: #35536d; font-weight: 600; letter-spacing: 0.01em; }

/* Amount cells */
.mst-amount { font-family: 'JetBrains Mono', monospace; font-size: 12.5px; font-weight: 700; }
.mst-amount.dep { color: #38bdf8; }
.mst-amount.wit { color: #a78bfa; }
.mst-amount.fee { color: #2a4a65; }
.mst-amount.net { color: #34d399; }
.mst-amount-chip {
  display: inline-flex; align-items: center; border-radius: 10px; padding: 5px 9px;
  background: rgba(0,0,0,0.2); border: 1px solid rgba(255,255,255,0.04);
}

/* Status badge */
.mst-status { display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 5px 12px; font-size: 11px; font-weight: 800; white-space: nowrap; }
.mst-s-completed  { background: rgba(16,185,129,0.1);  color: #34d399; border: 1px solid rgba(16,185,129,0.25); }
.mst-s-pending    { background: rgba(245,158,11,0.1);  color: #fbbf24; border: 1px solid rgba(245,158,11,0.22); }
.mst-s-processing { background: rgba(14,165,233,0.1);  color: #38bdf8; border: 1px solid rgba(14,165,233,0.22); }
.mst-s-failed     { background: rgba(239,68,68,0.1);   color: #f87171; border: 1px solid rgba(239,68,68,0.22); }

/* Count badge */
.mst-count {
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(34,211,238,0.08); border: 1px solid rgba(34,211,238,0.18);
  border-radius: 8px; padding: 3px 10px; min-width: 36px;
  font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 700; color: #22d3ee;
}
.mst-count-na {
  background: rgba(90,138,170,0.12); border-color: rgba(90,138,170,0.2); color: #5a8aaa;
}

/* Empty state */
.mst-empty { text-align: center; padding: 72px 24px; }
.mst-empty-icon { width: 68px; height: 68px; border-radius: 20px; background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.1); display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; }

/* Skeleton */
@keyframes mst-pulse { 0%,100%{opacity:0.3} 50%{opacity:0.7} }
.mst-skel { height: 56px; background: rgba(34,211,238,0.03); animation: mst-pulse 1.6s ease-in-out infinite; }

/* Pagination */
.mst-pag-btn {
  background: rgba(34,211,238,0.05); border: 1px solid rgba(34,211,238,0.1);
  border-radius: 10px; padding: 8px 12px; color: #5a8aaa; cursor: pointer; display: flex; align-items: center;
  transition: all 0.22s cubic-bezier(0.34,1.56,0.64,1);
}
.mst-pag-btn:hover:not(:disabled) { border-color: rgba(34,211,238,0.35); color: #22d3ee; box-shadow: 0 0 14px rgba(34,211,238,0.1); transform: translateY(-1px); }
.mst-pag-btn:active:not(:disabled) { transform: scale(0.95); }
.mst-pag-btn:disabled { opacity: 0.3; cursor: not-allowed; }

/* Responsive */
@media (max-width: 900px) { .mst-stat-grid { grid-template-columns: repeat(2,1fr) !important; } }
@media (max-width: 640px) {
  .mst-stat-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
  .mst-thead th:nth-child(4), .mst-td:nth-child(4),
  .mst-thead th:nth-child(5), .mst-td:nth-child(5) { display: none; }
  .mst-h1 { font-size: 1.4rem; }
}
@media (max-width: 420px) {
  .mst-stat-grid { grid-template-columns: 1fr !important; }
  .mst-thead th:nth-child(6), .mst-td:nth-child(6) { display: none; }
}
`;

const STATUS_CFG = {
  PENDING:    { icon: Clock,        cls: "mst-s-pending",    label: "Pending" },
  PROCESSING: { icon: Loader2,      cls: "mst-s-processing", label: "Processing" },
  COMPLETED:  { icon: CheckCircle2, cls: "mst-s-completed",  label: "Completed" },
  FAILED:     { icon: XCircle,      cls: "mst-s-failed",     label: "Failed" },
};

const STAT_DEFS = [
  { key: "total",   label: "Total Settled", icon: TrendingUp, bg: "linear-gradient(135deg,#10b981,#059669)", glow: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.35)", shadow: "rgba(16,185,129,0.15)", prefix: "฿" },
  { key: "pending", label: "Pending",       icon: Clock,      bg: "linear-gradient(135deg,#f59e0b,#d97706)", glow: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.35)", shadow: "rgba(245,158,11,0.15)", prefix: "฿" },
  { key: "count",   label: "Total Records", icon: BarChart3,  bg: "linear-gradient(135deg,#06b6d4,#0891b2)", glow: "rgba(6,182,212,0.1)",  border: "rgba(6,182,212,0.35)",  shadow: "rgba(6,182,212,0.15)", prefix: "" },
];

export default function MerchantSettlements() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const { data: settlementData, isLoading, isError, error } = useQuery({
    queryKey: ["merchant-settlements", search, statusFilter],
    queryFn: () => settlementService.getMerchantSettlements({
      page: 1,
      limit: 200,
      search: search || undefined,
      filters: statusFilter !== "all" ? { status: statusFilter } : undefined,
    }),
  });

  const settlements = settlementData?.data || [];

  const filtered = settlements.filter(s => {
    if (search) {
      const sl = search.toLowerCase();
      const idMatch = s.id?.toLowerCase().includes(sl);
      const codeMatch = s.merchant_code?.toLowerCase().includes(sl);
      if (!idMatch && !codeMatch) return false;
    }
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    return true;
  });

  const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  const stats = {
    total: settlements.filter(s => s.status === "COMPLETED").reduce((sum, s) => sum + (s.net_amount || 0), 0),
    pending: settlements.filter(s => ["PENDING", "PROCESSING"].includes(s.status)).reduce((sum, s) => sum + (s.net_amount || 0), 0),
    count: settlements.length,
  };

  const getTxnCount = (settlement) => {
    const count = settlement?.transaction_count ?? settlement?.transactionCount ?? null;
    return Number.isFinite(count) ? count : null;
  };

  return (
    <MerchantLayout currentPage="MerchantSettlements" merchantName="Royal Gaming" merchantCode="MRC001">
      <style>{CSS}</style>
      <div className="mst-root">
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -16, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: "flex", alignItems: "center", gap: 14 }}
          >
            <div style={{ width: 46, height: 46, borderRadius: 14, background: "linear-gradient(135deg, #10b981, #059669)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 24px rgba(16,185,129,0.4)", flexShrink: 0, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(145deg, rgba(255,255,255,0.12), transparent)" }} />
              <FileText size={20} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
            </div>
            <div>
              <h1 className="mst-h1">{t("settlementTitle") || "Settlements"}</h1>
              <p className="mst-sub">Track your payment settlement records</p>
            </div>
          </motion.div>

          {/* Stat cards */}
          <div className="mst-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
            {STAT_DEFS.map((s, i) => (
              <motion.div
                key={s.key}
                className="mst-stat"
                style={{ "--sg": s.glow, "--sb": s.border, "--ss": s.shadow }}
                initial={{ opacity: 0, y: 22, scale: 0.93, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                transition={{ delay: i * 0.07, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -5 }}
              >
                <div className="mst-stat-icon" style={{ background: s.bg, boxShadow: `0 6px 20px ${s.shadow}` }}>
                  <s.icon size={18} style={{ color: "#fff", position: "relative", zIndex: 1 }} />
                </div>
                <div className="mst-stat-lbl">{s.label}</div>
                <div className="mst-stat-val">{s.prefix}{typeof stats[s.key] === "number" ? stats[s.key].toLocaleString() : stats[s.key]}</div>
              </motion.div>
            ))}
          </div>

          {/* Filter bar */}
          <motion.div className="mst-filter"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <div style={{ color: "#22d3ee" }}><Filter size={14} /></div>
            <div className="mst-search" style={{ flex: 1, minWidth: 200 }}>
              <Search size={14} className="mst-search-icon" />
              <input
                placeholder={t("searchBySettlementId") || "Search by Settlement ID…"}
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
              />
              <AnimatePresence>
                {search && (
                  <motion.button initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                    onClick={() => setSearch("")}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 20, height: 20, borderRadius: 6, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#3a5a78" }}
                  >
                    <X size={10} />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="mst-sel" style={{ width: 160, height: 42 }}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent style={{ background: "rgba(6,14,30,0.98)", border: "1px solid rgba(34,211,238,0.15)", borderRadius: 14, color: "#e2f4ff" }}>
                <SelectItem value="all">{t("allStatus") || "All Status"}</SelectItem>
                  <SelectItem value="COMPLETED">{t("completed") || "Completed"}</SelectItem>
                  <SelectItem value="PENDING">{t("pending") || "Pending"}</SelectItem>
                  <SelectItem value="PROCESSING">{t("processing") || "Processing"}</SelectItem>
                  <SelectItem value="FAILED">{t("failed") || "Failed"}</SelectItem>
              </SelectContent>
            </Select>
          </motion.div>

          {/* Table */}
          <motion.div className="mst-table-card"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.45 }}
          >
            <div style={{ overflowX: "auto" }}>
              <table className="mst-table">
                <thead className="mst-thead">
                  <tr>
                    <th>{t("settlementId") || "Settlement ID"}</th>
                    <th>{t("period") || "Period"}</th>
                    <th>{t("transactions") || "Txns"}</th>
                    <th>{t("deposits") || "Deposits"}</th>
                    <th>{t("withdrawals") || "Withdrawals"}</th>
                    <th>{t("totalFees") || "Fees"}</th>
                    <th>{t("netAmount") || "Net Amount"}</th>
                    <th>{t("status") || "Status"}</th>
                  </tr>
                </thead>
                <tbody>
                  {isError && (
                    <tr>
                      <td className="mst-td" colSpan={8}>
                        <div style={{ color: "#f87171", fontSize: 13, fontWeight: 700, padding: "16px 0" }}>
                          Failed to load settlements: {error?.message || "Unknown error"}
                        </div>
                      </td>
                    </tr>
                  )}
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="mst-row">
                        <td className="mst-td" colSpan={8}><div className="mst-skel" style={{ borderRadius: 8 }} /></td>
                      </tr>
                    ))
                  ) : paginated.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <div className="mst-empty">
                          <div className="mst-empty-icon"><FileText size={28} style={{ color: "#1e3a52" }} /></div>
                          <p style={{ color: "#2a4a65", fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{t("noSettlements") || "No settlements found"}</p>
                          <p style={{ color: "#1e3a50", fontSize: 12, fontWeight: 500 }}>Settlement records will appear here once processed.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginated.map((s, idx) => {
                      const st = STATUS_CFG[s.status] || STATUS_CFG.PENDING;
                      const StIcon = st.icon;
                      return (
                        <motion.tr key={s.id} className="mst-row"
                          initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.025 }}
                        >
                          <td className="mst-td">
                            <span className="mst-sid mst-sid-pill" title={s.id}>{s.id}</span>
                          </td>
                          <td className="mst-td">
                            <div className="mst-period">
                              <Calendar size={11} />
                              {s.period_start ? format(new Date(s.period_start), "MMM d") : "—"}
                              {s.period_end ? ` - ${format(new Date(s.period_end), "MMM d")}` : ""}
                            </div>
                            <div className="mst-period-sub">
                              {s.period_start ? format(new Date(s.period_start), "yyyy") : ""}
                            </div>
                          </td>
                          <td className="mst-td">
                            {getTxnCount(s) === null ? (
                              <span className="mst-count mst-count-na" title="Transaction count not available for this settlement">N/A</span>
                            ) : (
                              <span className="mst-count">{getTxnCount(s)}</span>
                            )}
                          </td>
                          <td className="mst-td"><span className="mst-amount-chip"><span className="mst-amount dep">฿{(s.total_deposits || 0).toLocaleString()}</span></span></td>
                          <td className="mst-td"><span className="mst-amount-chip"><span className="mst-amount wit">฿{(s.total_withdrawals || 0).toLocaleString()}</span></span></td>
                          <td className="mst-td"><span className="mst-amount-chip"><span className="mst-amount fee">฿{(s.total_fees || 0).toLocaleString()}</span></span></td>
                          <td className="mst-td"><span className="mst-amount-chip"><span className="mst-amount net">฿{(s.net_amount || 0).toLocaleString()}</span></span></td>
                          <td className="mst-td">
                            <span className={`mst-status ${st.cls}`}>
                              <StIcon size={11} style={s.status === "PROCESSING" ? { animation: "spin 1.2s linear infinite" } : {}} />
                              {st.label}
                            </span>
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}
            >
              <p style={{ color: "#2a4a65", fontSize: 13, fontWeight: 600 }}>
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of {filtered.length}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <motion.button className="mst-pag-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0} whileTap={page > 0 ? { scale: 0.95 } : {}}>
                  <ChevronLeft size={16} />
                </motion.button>
                <span style={{ color: "#5a8aaa", fontSize: 13, fontWeight: 700, padding: "0 6px" }}>
                  {page + 1} / {totalPages}
                </span>
                <motion.button className="mst-pag-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} whileTap={page < totalPages - 1 ? { scale: 0.95 } : {}}>
                  <ChevronRight size={16} />
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </MerchantLayout>
  );
}